/**
 * Palet warna buatan pengguna.
 *
 * Dua puluh enam ramp bawaan — keluarga Petrel, keluarga OpendTect, dan
 * beberapa yang lahir di sini — sudah menutup hampir semua yang biasa diminta.
 * Yang tidak tertutup adalah palet rumah: sebuah perusahaan yang mencetak
 * lembar porositasnya dengan tiga warna tertentu sejak sepuluh tahun lalu tidak
 * sedang mencari ramp yang lebih baik, melainkan ramp yang sama dengan lembar
 * di dinding. Ramp bawaan tidak bisa menyediakan itu, berapa pun jumlahnya.
 *
 * Karena itu palet di sini adalah data, bukan kode: dibuat, disunting, dan
 * dibuang oleh penggunanya, lalu disimpan di peramban seperti setelan kurva
 * lembar korelasi. Kuncinya berawalan `user_` dan itu bukan hiasan — ia yang
 * menjamin sebuah palet rumah tidak pernah menimpa `pt_porosity`, sehingga
 * lembar yang menyebut ramp bawaan tetap menggambar ramp bawaan sekalipun ada
 * palet rumah bernama sama.
 *
 * Yang **tidak** dilakukan modul ini: menghapus atau menimpa ramp bawaan.
 * Palet rumah menambah pilihan, tidak mengurangi. Sebuah lembar lama yang
 * menyebut `spectrum` harus tetap terbuka sama persis di komputer yang paletnya
 * berbeda — dan satu-satunya cara menjamin itu adalah membuat tabel bawaan
 * tidak bisa disentuh dari sini.
 */
import type { Stops } from "./colorscale";

export interface UserRamp {
  /** Selalu berawalan `user_`; lihat `isUserRamp`. */
  key: string;
  label: string;
  stops: Stops;
}

export const USER_RAMP_PREFIX = "user_";

/** Kunci penyimpanan di peramban. */
export const USER_RAMP_STORE = "evolog.ramps.user";

/**
 * Jumlah stop terkecil dan terbesar untuk satu palet.
 *
 * Dua adalah batas bawah yang sesungguhnya: satu stop bukan gradasi melainkan
 * warna rata, dan `rampColor` membaginya dengan `stops.length - 1` — nol.
 * Batas atasnya kelonggaran, bukan keharusan: dua belas stop sudah lebih rapat
 * daripada yang bisa dibedakan mata pada pita selebar satu track, dan yang
 * lebih dari itu hanya membuat penyuntingnya tidak bisa dipakai.
 */
export const MIN_STOPS = 2;
export const MAX_STOPS = 12;

export function isUserRamp(key: string): boolean {
  return key.startsWith(USER_RAMP_PREFIX);
}

/** `#rrggbb` menjadi tiga bilangan bulat; `null` bila bukan warna. */
export function parseHex(hex: string): [number, number, number] | null {
  const cocok = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!cocok) return null;
  const n = parseInt(cocok[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Tiga bilangan bulat menjadi `#rrggbb`, sebagaimana pemilih warna memintanya. */
export function toHex([r, g, b]: [number, number, number]): string {
  const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v)));
  return `#${[r, g, b].map((v) => clamp(v).toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Kunci baru yang belum dipakai.
 *
 * Berdasar waktu dan bukan nomor urut: dua peramban yang menyusun daftarnya
 * masing-masing akan sama-sama sampai pada `user_3`, dan begitu keduanya
 * bertemu — lewat berkas yang diimpor, atau setelan yang disalin — salah satu
 * palet menimpa yang lain tanpa satu pun keluhan. Akhiran acaknya menutup
 * kemungkinan dua palet lahir pada milidetik yang sama.
 */
export function newRampKey(now: number = Date.now()): string {
  const acak = Math.random().toString(36).slice(2, 7);
  return `${USER_RAMP_PREFIX}${now.toString(36)}${acak}`;
}

/** Palet baru dengan dua stop — cukup untuk menjadi gradasi, tidak lebih. */
export function newUserRamp(label = "New palette"): UserRamp {
  return {
    key: newRampKey(),
    label,
    stops: [
      [240, 240, 240],
      [40, 70, 150],
    ],
  };
}

/**
 * Salinan sebuah ramp — bawaan maupun milik pengguna — sebagai palet baru.
 *
 * Ramp bawaan tidak bisa disunting, dan memang tidak boleh: lembar yang
 * menyebut `spectrum` harus menggambar `spectrum` yang sama di komputer mana
 * pun. Yang ingin mengubah warnanya menyunting salinannya. Ramp yang stopnya
 * melebihi `MAX_STOPS` dicuplik ulang secara merata, sehingga salinannya
 * tergambar sama dengan asalnya sejauh dua belas stop bisa menyatakannya.
 */
export function copyOfRamp(
  label: string,
  stops: [number, number, number][],
): UserRamp {
  const n = Math.min(MAX_STOPS, Math.max(MIN_STOPS, stops.length));
  const cuplik = (t: number): [number, number, number] => {
    if (stops.length === 1) return [...stops[0]] as [number, number, number];
    const x = t * (stops.length - 1);
    const i = Math.min(stops.length - 2, Math.floor(x));
    const f = x - i;
    const [a, b] = [stops[i], stops[i + 1]];
    return [0, 1, 2].map((k) => Math.round(a[k] + (b[k] - a[k]) * f)) as [number, number, number];
  };
  return {
    key: newRampKey(),
    label,
    stops: stops.length <= MAX_STOPS && stops.length >= MIN_STOPS
      ? stops.map((stop) => [...stop] as [number, number, number])
      : Array.from({ length: n }, (_, i) => cuplik(i / (n - 1))),
  };
}

/** Satu stop yang sah, atau `null`. */
function readStop(value: unknown): [number, number, number] | null {
  if (typeof value === "string") return parseHex(value);
  if (!Array.isArray(value) || value.length < 3) return null;
  const [r, g, b] = value;
  if (![r, g, b].every((v) => typeof v === "number" && Number.isFinite(v))) return null;
  const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v as number)));
  return [clamp(r as number), clamp(g as number), clamp(b as number)];
}

/**
 * Daftar palet yang sah dari apa pun yang tersimpan.
 *
 * Yang tersimpan di peramban bukan data yang dijamin siapa pun: ia berumur
 * lebih panjang daripada versi aplikasi yang menuliskannya, dan bisa disunting
 * dengan tangan. Palet yang cacat dibuang satu per satu alih-alih membatalkan
 * seluruh daftarnya — satu entri rusak tidak boleh menghapus sembilan palet
 * rumah yang masih baik.
 */
export function normalizeUserRamps(raw: unknown): UserRamp[] {
  if (!Array.isArray(raw)) return [];
  const dipakai = new Set<string>();
  const hasil: UserRamp[] = [];
  for (const entri of raw) {
    if (!entri || typeof entri !== "object") continue;
    const { key, label, stops } = entri as Record<string, unknown>;
    if (typeof key !== "string" || !isUserRamp(key) || dipakai.has(key)) continue;
    if (!Array.isArray(stops)) continue;
    const dibaca = stops.map(readStop).filter((s): s is [number, number, number] => s !== null);
    if (dibaca.length < MIN_STOPS) continue;
    dipakai.add(key);
    hasil.push({
      key,
      label: typeof label === "string" && label.trim() ? label.trim() : "Palet tanpa nama",
      stops: dibaca.slice(0, MAX_STOPS),
    });
  }
  return hasil;
}

/** Menambah palet, atau mengganti yang sudah ada dengan kunci yang sama. */
export function upsertUserRamp(list: UserRamp[], ramp: UserRamp): UserRamp[] {
  const index = list.findIndex((r) => r.key === ramp.key);
  if (index < 0) return [...list, ramp];
  const salinan = list.slice();
  salinan[index] = ramp;
  return salinan;
}

export function removeUserRamp(list: UserRamp[], key: string): UserRamp[] {
  return list.filter((r) => r.key !== key);
}

/**
 * Menyisipkan sebuah stop di antara dua stop yang berdampingan.
 *
 * Warnanya diambil dari tengah keduanya, jadi menambah stop tidak mengubah
 * rupa palet sedikit pun sampai stop itu digeser. Menambah stop yang langsung
 * mengubah gradasinya membuat penggunanya harus membetulkan dua hal sekaligus:
 * warna yang baru, dan kerusakan yang ditimbulkannya.
 */
export function insertStop(stops: Stops, after: number): Stops {
  if (stops.length >= MAX_STOPS) return stops;
  const i = Math.min(Math.max(0, after), stops.length - 1);
  const a = stops[i];
  const b = stops[Math.min(stops.length - 1, i + 1)];
  const tengah: [number, number, number] = [
    Math.round((a[0] + b[0]) / 2),
    Math.round((a[1] + b[1]) / 2),
    Math.round((a[2] + b[2]) / 2),
  ];
  return [...stops.slice(0, i + 1), tengah, ...stops.slice(i + 1)];
}

/** Membuang satu stop, selama masih tersisa cukup untuk sebuah gradasi. */
export function removeStop(stops: Stops, index: number): Stops {
  if (stops.length <= MIN_STOPS) return stops;
  if (index < 0 || index >= stops.length) return stops;
  return [...stops.slice(0, index), ...stops.slice(index + 1)];
}

/** Mengganti warna satu stop. */
export function setStop(stops: Stops, index: number, colour: [number, number, number]): Stops {
  if (index < 0 || index >= stops.length) return stops;
  const salinan = stops.slice();
  salinan[index] = colour;
  return salinan;
}

/** Membalik urutan stop — ujung terang menjadi ujung gelap. */
export function reverseStops(stops: Stops): Stops {
  return [...stops].reverse();
}
